/**
 * HomeDashboardCardsPage — Reference · Home Dashboard Cards.
 *
 * Per-(role × card) config for the Home Landing dashboard (Screen 44). Each row
 * places a card for a role, with an optional label override and drill-through
 * route; displayOrder sorts, visible toggles.
 *
 * Contract (verified 2026-06-16 — AdminComplianceService upsert persists every
 * field; composite natural key Role+CardKey):
 *   - Read:   GET    /v3/admin/home-dashboard-card
 *   - Write:  POST   /v3/admin/home-dashboard-card
 *   - Delete: DELETE /v3/admin/home-dashboard-card/{role}/{cardKey}
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

const ROLES = ['FSR', 'Officer', 'Admin', 'SuperAdmin'] as const;

interface Row {
  role: string;
  cardKey: string;
  displayOrder: number;
  visible: boolean;
  labelOverride: string | null;
  drillRoute: string | null;
}

interface EditDraft extends Row {
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return { role: 'FSR', cardKey: '', displayOrder: 100, visible: true, labelOverride: '', drillRoute: '', isNew: true };
}

export function HomeDashboardCardsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const r = await client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
          '/v3/admin/home-dashboard-card',
        );
        if (cancelled) return;
        if (r.success && Array.isArray(r.data)) setRows(r.data);
        else setLoadError(r.error ?? 'Failed to load cards.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const list = (rows ?? []).slice().sort((a, b) => a.role.localeCompare(b.role) || a.displayOrder - b.displayOrder);
    return roleFilter ? list.filter((r) => r.role === roleFilter) : list;
  }, [rows, roleFilter]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.role.trim() || !draft.cardKey.trim()) {
      setSaveError('Role and card key are both required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.post<{ success: boolean; error?: string }>('/v3/admin/home-dashboard-card', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body: { ...draft, labelOverride: draft.labelOverride?.trim() || null, drillRoute: draft.drillRoute?.trim() || null },
      });
      if (!r.success) throw new Error(r.error ?? 'save failed');
      setDraft(null);
      setToast({ tone: 'info', text: `Saved ${draft.role}/${draft.cardKey}.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.del<{ success: boolean; error?: string }>(
        `/v3/admin/home-dashboard-card/${encodeURIComponent(deleteTarget.role)}/${encodeURIComponent(deleteTarget.cardKey)}`,
        { headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL) },
      );
      if (!r.success) throw new Error(r.error ?? 'delete failed');
      setToast({ tone: 'warn', text: `Deleted ${deleteTarget.role}/${deleteTarget.cardKey}.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = [
    { header: 'Role', cell: (r) => <code className="cell-mono">{r.role}</code> },
    { header: 'Card', cell: (r) => <code className="cell-mono--bold">{r.cardKey}</code> },
    { header: 'Order', cell: (r) => <span className="num">{r.displayOrder}</span> },
    { header: 'Visible', cell: (r) => (r.visible ? <Pill tone="ok">visible</Pill> : <Pill tone="muted">hidden</Pill>) },
    { header: 'Label', cell: (r) => <span className="cell-desc">{r.labelOverride || '—'}</span> },
    { header: 'Drill route', cell: (r) => <code className="cell-mono">{r.drillRoute || '—'}</code> },
    {
      header: '',
      cell: (r) => (
        <div className="card-actions-row">
          <Button onClick={() => { setDraft({ ...r, labelOverride: r.labelOverride ?? '', drillRoute: r.drillRoute ?? '', isNew: false }); setSaveError(null); }}>Edit</Button>
          <Button onClick={() => setDeleteTarget(r)}>Delete</Button>
        </div>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load dashboard cards: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading home dashboard cards…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Home Dashboard Cards — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={roleFilter}
                onChange={setRoleFilter}
                options={[{ value: '', label: 'All roles' }, ...ROLES.map((r) => ({ value: r, label: r }))]}
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + New card
            </Button>
          </div>
        }
      >
        <p className="cell-desc">Per-(role × card) config for the Home Landing dashboard (Screen 44).</p>
        <DataTable<Row>
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.role}::${r.cardKey}`}
          emptyState={<p className="empty">No cards match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'New card' : `Edit ${draft?.role}/${draft?.cardKey}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <div className="form-row">
              <SelectField
                label="Role"
                value={draft.role}
                onChange={(v) => setDraft({ ...draft, role: v })}
                disabled={!draft.isNew}
                options={ROLES.map((r) => ({ value: r, label: r }))}
              />
              <TextField
                label="Card key"
                value={draft.cardKey}
                onChange={(v) => draft.isNew && setDraft({ ...draft, cardKey: v })}
                disabled={!draft.isNew}
                monospace
                placeholder="MyCarts / Tasks / Approvals"
              />
            </div>
            <div className="form-row">
              <NumberField
                label="Display order"
                value={draft.displayOrder}
                onChange={(v) => setDraft({ ...draft, displayOrder: v ?? 100 })}
                min={0}
              />
              <BoolField label="Visible" value={draft.visible} onChange={(v) => setDraft({ ...draft, visible: v })} />
            </div>
            <TextField
              label="Label override (optional)"
              value={draft.labelOverride ?? ''}
              onChange={(v) => setDraft({ ...draft, labelOverride: v })}
            />
            <TextField
              label="Drill route (optional)"
              value={draft.drillRoute ?? ''}
              onChange={(v) => setDraft({ ...draft, drillRoute: v })}
              monospace
              placeholder="/carts/mine"
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Delete this card?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={480}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Delete'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              Delete card <code className="cell-mono">{deleteTarget.role}/{deleteTarget.cardKey}</code>?
              It will stop rendering on that role's Home dashboard.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
